import { createRef, useRef } from 'react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import smoothscroll from 'smoothscroll-polyfill';

import IconMerPay from '@/assets/icons/icon_app_merpay.svg';
import IconPayPay from '@/assets/icons/icon_app_paypay.svg';
import IconBank from '@/assets/icons/icon_bank_transfer.svg';
import IconCard from '@/assets/icons/icon_card_normal.svg';
import IconConveni from '@/assets/icons/icon_conveni.svg';
import IconWallet from '@/assets/icons/icon_wallet.svg';
import Button from '@/components/elements/Button/BaseButton';
import Image from '@/components/elements/Image';
import { TextExternalLink } from '@/components/elements/Text/TextLink';
import GuideToretaNotesList from '@/components/layouts/Guide/Toreta/NotesList';
import { TORETA_PAY_HELP_URL } from '@/config/constants/guide';
import { useUserContext } from '@/contexts/user';
import { GuidePageTitles } from '@/pages/Guide/types';

import {
  Box,
  BoxImage,
  ButtonContainer,
  Container,
  ContentBox,
  Text,
  TextLarge,
  TextLink,
  TextNote,
  Title,
  TitleBox,
  TitleSecondary,
} from '../style';
import {
  ChargeTypes,
  Note,
  TextNoteToreta,
  ToretaNavi,
  ToretaNaviButton,
  ToretaNaviImage,
  ToretaNaviText,
} from './style';

smoothscroll.polyfill();

const getButtonElement = (label: string) => (
  <Button
    type="button"
    outline
    bgColor="primary"
    size="m"
    radius="l"
    height="56px"
    fontWeight="bold"
  >
    {label}
  </Button>
);

type BottomNotesType = string[] | (string | (string | JSX.Element)[]);
type DataType = {
  image: any;
  title: string;
  titleNavi?: string | (string | (string | JSX.Element))[];
  text: string | (string | (string | JSX.Element))[];
  linkText?: string;
  linkUrl?: string;
  externalLinkText?: string;
  externalLinkUrl?: string;
  button?: JSX.Element;
  bottomNotes?: BottomNotesType[];
};

const data: DataType[] = [
  {
    image: IconCard,
    title: 'クレジットカード',
    text: [
      '登録しておくといつでもチャージできます。',
      <br key="br1" />,
      '投票時に不足分だけチャージすることも可能なのでスムーズに購入したい方へおすすめです。',
    ],
    externalLinkText: 'カード情報の登録 / 変更はこちら',
    externalLinkUrl: import.meta.env.VITE_DMM_PAYMENT_URL,
    bottomNotes: [
      '3Dセキュア（本人認証サービス）が設定済みのクレジットカードのみご利用いただけます。',
    ],
  },
  {
    image: IconBank,
    title: '銀行振込',
    text: 'インターネットバンキングやATMからの振込によるチャージが可能です。',
  },
  {
    image: IconConveni,
    title: 'コンビニで購入',
    text: '対象コンビニの店頭端末で「toreta+ 番号」を購入し、番号を入力することでチャージができます。',
    linkText: '詳しくはこちら',
    linkUrl: '/news/info_conveniencestore',
  },
  {
    image: IconPayPay,
    title: 'PayPay',
    text: 'PayPayの残高からチャージができます。',
    externalLinkText: '詳しくはこちら',
    externalLinkUrl: TORETA_PAY_HELP_URL,
  },
  {
    image: IconMerPay,
    title: 'メルペイ',
    text: 'メルペイの残高やポイント、メルペイスマート払いをご利用いただけます。',
    externalLinkText: '詳しくはこちら',
    externalLinkUrl: TORETA_PAY_HELP_URL,
  },
  {
    image: IconWallet,
    title: '入金/払戻金残高からチャージ',
    titleNavi: ['入金/払戻金', <br key="br1" />, '残高'],
    text: [
      '「入金/払戻金残高」を精算する際に、toreta+ へのチャージも可能です。',
      <br key="br1" />,
      '入金/払戻金残高への「入金」は銀行口座やATMから可能です。的中時の払戻金も入金/払戻金残高へ自動的に入金されます。',
    ],
    button: <Link to="/wallet">{getButtonElement('入金・精算ページへ')}</Link>,
  },
];

const GuideToretaTemplate: React.FC = () => {
  const { isMember } = useUserContext();
  const refs = useRef(data.map(() => createRef<HTMLLIElement>()));
  const handleClick = (i: number) => {
    window.scrollTo({
      top: refs.current[i].current?.offsetTop,
      behavior: 'smooth',
    });
  };

  return (
    <Container>
      <ContentBox>
        <Title>{GuidePageTitles.Toreta}</Title>
        <TextLarge>
          toreta+（トレタプラス）はDMM競輪で投票を行う際に使用する専用の電子マネーです。
        </TextLarge>
      </ContentBox>
      <ContentBox>
        <TitleSecondary>toreta+ チャージ方法</TitleSecondary>
        <TextLarge>toreta+ は以下の方法でチャージができます。</TextLarge>
        <TextNote>
          <i>※</i>toreta+ チャージはDMM競輪会員のみ行えます。
        </TextNote>
        <ToretaNavi>
          {data.map((item, i) => (
            <li key={i}>
              <ToretaNaviButton
                type="button"
                isWide={i === 0}
                onClick={() => handleClick(i)}
              >
                <ToretaNaviImage isWide={i === 0}>
                  <Image
                    srcSet={item.image}
                    alt=""
                    width="48px"
                    height="48px"
                  />
                </ToretaNaviImage>
                <ToretaNaviText>{item.titleNavi || item.title}</ToretaNaviText>
              </ToretaNaviButton>
            </li>
          ))}
        </ToretaNavi>
        <ChargeTypes>
          {data.map((item, i) => (
            <li key={i} ref={refs.current[i]}>
              <Box>
                <BoxImage>
                  <Image
                    srcSet={item.image}
                    alt=""
                    width="80px"
                    height="80px"
                  />
                </BoxImage>
                <div>
                  <TitleBox>{item.title}</TitleBox>
                  <Text>
                    {item.text}
                    {item.linkUrl && (
                      <>
                        <br />
                        <TextLink to={item.linkUrl}>{item.linkText}</TextLink>
                      </>
                    )}
                    {item.externalLinkUrl && (
                      <>
                        <br />
                        <TextExternalLink
                          href={item.externalLinkUrl}
                          fontWeight="normal"
                        >
                          {item.externalLinkText}
                        </TextExternalLink>
                      </>
                    )}
                  </Text>
                  {item.title === 'クレジットカード' && (
                    <Text>
                      VISA、JCB<sup>※1</sup>
                      <Note num={1}>
                        DMMカードを含むポケットカード発行のJCBカードのみ対応
                      </Note>
                    </Text>
                  )}
                </div>
              </Box>
              {(item.button || isMember) && (
                <ButtonContainer>{item.button}</ButtonContainer>
              )}
              {item.bottomNotes && (
                <div>
                  {item.bottomNotes.map((note: BottomNotesType, j: number) => (
                    <TextNoteToreta key={j}>
                      <span>{note}</span>
                    </TextNoteToreta>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ChargeTypes>
      </ContentBox>
      <ContentBox>
        <GuideToretaNotesList />
      </ContentBox>
    </Container>
  );
};

export default GuideToretaTemplate;
